/**
 * useUrlFilters — URL-synced filter state with live-apply (v1.4 dashboard UX
 * fix / Bug F1) and same-tab persistence (Bug D16, revised Overview P1-2).
 *
 * contract:
 *   - URL is parsed via `qs` (comma arrays) through `FilterSchema.safeParse`.
 *   - Invalid URLs fall back to `DEFAULT_FILTERS` — never throws.
 *   - `setPending(patch)` writes the merged filter straight to the URL — there
 *     is no staging buffer any more; every control edit triggers an immediate
 *     refetch (TanStack Query dedupes rapid successive calls, so the extra
 *     traffic is cheap).
 *   - `stripDefaults()` trims the emitted URL so shared links stay short.
 *
 * Persistence (Bug D16, revised for Overview P1-2):
 *   - Every commit writes the current filters to BOTH `localStorage` (long-
 *     lived, survives restart) AND `sessionStorage` (same-tab only). The
 *     sessionStorage entry is the canonical "this tab is mid-session" flag
 *     used for auto-rehydration — localStorage is kept as a reference for
 *     a future explicit "Restore last session" affordance.
 *   - On first mount of the FIRST hook instance in the app (module-level
 *     flag, not per-hook-instance ref), if the URL has NO filter params
 *     (bare `/overview`), the hook reads `sessionStorage[StorageKey.FILTERS]`
 *     and hydrates the URL from it. sessionStorage is per-tab, so a deep
 *     link opened in a NEW tab (or a colleague's browser) always resolves
 *     to `DEFAULT_FILTERS` — shared URLs now behave identically for every
 *     recipient regardless of what their localStorage contains.
 *   - Overview P1-2 regression fix (2026-04-23): the previous implementation
 *     hydrated from localStorage, which broke copy-URL sharing: a user who
 *     had `dayFilter=weekdays` cached in their localStorage would see
 *     weekday-only data on every "bare" URL regardless of what the sender
 *     meant. Gating the auto-rehydrate on sessionStorage scopes the
 *     convenience to the tab that last used it, which is what the user
 *     expected from "remember my last filters" anyway (cross-browser /
 *     cross-machine persistence was never promised).
 *   - URL always wins over storage: a deep link like `/overview?role=rm`
 *     is never overridden by a conflicting entry in storage.
 *   - Schema drift: if the stored JSON fails zod parse against the current
 *     FilterSchema, the entry is dropped silently and DEFAULT_FILTERS apply.
 *
 * BUG-1 (user report 2026-04-23) — "URL changes but filters don't toggle":
 *   - Root cause 1: hydration used `useRef(false)` PER-HOOK-INSTANCE. Every
 *     page mounts its own `useUrlFilters` (FilterBar + page + SortableTable
 *     + use-user-detail), and each tab switch unmounts/remounts the page's
 *     hook with a fresh `hydratedRef=false`. The next time the URL briefly
 *     looked empty (mid-navigation or a subsequent hook re-mount on a new
 *     route), the effect re-ran and overwrote the URL the user had just
 *     typed into. A module-level flag guarantees hydration runs exactly
 *     ONCE per page load.
 *   - Root cause 2: the cross-tab `storage` event listener blindly wrote
 *     `setSearchParams` whenever any other tab edited filters. In
 *     multi-tab setups, editing filters in tab A overwrote tab B's URL
 *     during the user's click — which is exactly the "URL changed but the
 *     control didn't react" symptom. Removed entirely — per-tab filter
 *     divergence is a feature, not a bug.
 *
 * Legacy shape (`pendingFilters`, `apply`, `dirty`) is kept to avoid a
 * fan-out rename across FilterBar / FilterModal consumers: `pendingFilters`
 * now always equals `filters`, `apply()` is a no-op, `dirty` is always `false`.
 * The Apply button is hidden in FilterBar / FilterModal — the hook fields
 * only remain for backwards-compatible interface.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import qs from 'qs';

import { StorageKey } from '@/constants/storageKeys';
import {
  DEFAULT_FILTERS,
  FilterSchema,
  type Filters,
  parseSortKeys,
  stripDefaults,
} from '@/lib/filters-schema';

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

/**
 * Parse a raw `location.search` string (with or without leading `?`) into
 * `Filters`. Uses `qs` with `comma: true` so `users=alice,bob` becomes
 * `['alice','bob']`.
 *
 * Why we read the raw search string instead of `URLSearchParams.toString()`:
 * `URLSearchParams.toString()` percent-encodes commas as `%2C`, and `qs`
 * decodes *after* splitting on comma — so an encoded comma never produces
 * an array. Passing the original `location.search` verbatim keeps the
 * encoding qs expects.
 *
 * The parsed object is layered *over* `DEFAULT_FILTERS` before zod-validation
 * so an empty URL still yields the canonical DEFAULT_FILTERS (including
 * `preset: '30d'`), and `filters` is always a complete, resolvable state.
 *
 * Any zod validation miss → fall back to `DEFAULT_FILTERS`; the URL is
 * user-editable and must be resilient.
 */
function parseFromSearch(search: string): Filters {
  const stripped = search.startsWith('?') ? search.slice(1) : search;
  const parsed = qs.parse(stripped, { comma: true }) as Record<string, unknown>;
  // Project any `<tableId>_sort=<col>.<dir>` keys into the `sort` shape.
  // The raw `*_sort` keys themselves don't live on FilterSchema, so they'd
  // be stripped by zod — parse them before the schema pass.
  const sort = parseSortKeys(parsed);
  const merged = { ...DEFAULT_FILTERS, ...parsed };
  if (sort) (merged as Record<string, unknown>).sort = sort;
  const result = FilterSchema.safeParse(merged);
  if (!result.success) return DEFAULT_FILTERS;
  return result.data;
}

/**
 * Build the next URLSearchParams. Calls `stripDefaults` so only changed
 * fields are emitted, then serialises with `arrayFormat:'comma'`. Returns
 * `URLSearchParams` because `setSearchParams` accepts that shape.
 */
function writeToUrl(f: Filters): URLSearchParams {
  const stripped = stripDefaults(f);
  const str = qs.stringify(stripped, { arrayFormat: 'comma' });
  return new URLSearchParams(str);
}

/**
 * Static set of URL keys owned by this hook (Filters schema). Anything else
 * that lives in the URL — feedback-tab state (sort/limit/offset/…), user
 * drilldown deep-link params, etc. — is owned by a sibling hook and MUST
 * survive every write from here. See `mergeFiltersIntoParams` below.
 *
 * BUG dayFilter-toggle (v1.4): `dayFilter` must be in this set so switching
 * back to the default ('all', 2026-06) scrubs the stale param from the URL.
 * `mergeFiltersIntoParams` deletes every key in this set before re-writing
 * stripDefaults's output; if `dayFilter` is missing, a prior non-default
 * value (e.g. `dayFilter=weekdays`) sticks on the URL even though the next
 * write emitted no `dayFilter` key (because 'all' IS the default and
 * stripDefaults drops it). The segmented control then looks "stuck" — the
 * control reads from the URL, sees the stale value, and never visibly flips
 * back.
 *
 * DERIVED from `DEFAULT_FILTERS`, not listed by hand (2026-08-19). The hand
 * list was a second place to remember a new filter, and forgetting it fails
 * in the quietest possible way: the control works on the way OUT and sticks
 * on the way BACK, on Reset as well as on a click. `admins` was added and
 * missed exactly that way. Every field the schema owns is in
 * `DEFAULT_FILTERS` by construction — including `includeWeekends`, the
 * legacy read-path key, which is a derived mirror there. The two additions
 * below are the keys that exist on the wire but have no default: `from` /
 * `to` are emitted only for `preset === 'custom'`.
 */
const FILTER_URL_KEYS: ReadonlySet<string> = new Set([
  ...Object.keys(DEFAULT_FILTERS),
  'from',
  'to',
]);

/** True for any `*_sort` key — per-table sort state flattened by stripDefaults. */
function isTableSortKey(key: string): boolean {
  return key.endsWith('_sort');
}

/** True when `key` is owned by the Filters schema. */
function isFilterKey(key: string): boolean {
  return FILTER_URL_KEYS.has(key) || isTableSortKey(key);
}

/**
 * BUG F2 / filter-first-click: merge the stripped filter payload for `f`
 * INTO `prev`, overwriting only filter-owned keys and leaving every other
 * key intact. Previously we built a fresh `URLSearchParams` from
 * `writeToUrl(f)` and handed it to `setSearchParams` — that wiped any
 * sibling-hook state (Feedback tab's sort/limit/offset, etc.) on every
 * global-filter edit. Sibling hooks then re-rendered with a URL they no
 * longer recognised, so their first follow-up click appeared to do
 * nothing (the click wrote `sort=best` onto the stripped URL, but the
 * next global-filter tick overwrote it again). Using a functional
 * setSearchParams + this merger makes the two hooks compose without
 * clobbering each other.
 */
function mergeFiltersIntoParams(
  prev: URLSearchParams,
  f: Filters,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  // Drop every filter-owned key first so a transition from "non-default"
  // to "default" cleans up (e.g. role=rm → role=all should remove role).
  for (const key of Array.from(next.keys())) {
    if (isFilterKey(key)) next.delete(key);
  }
  // Emit the stripped shape (only non-default keys), preserving qs's
  // comma-array encoding for `users`.
  const stripped = stripDefaults(f);
  const str = qs.stringify(stripped, { arrayFormat: 'comma' });
  const fresh = new URLSearchParams(str);
  for (const [k, v] of fresh.entries()) {
    next.set(k, v);
  }
  return next;
}

// ---------------------------------------------------------------------------
// localStorage helpers (Bug D16)
// ---------------------------------------------------------------------------

/**
 * Read + zod-validate the stored filters from a given Storage instance.
 * Returns null on missing / drift / sandboxed access.
 *
 * Overview P1-2 (2026-04-23): split from a localStorage-hardcoded helper
 * so hydration can read from sessionStorage (per-tab, safe for copy-URL)
 * while the write-back still mirrors to localStorage for a future explicit
 * "Restore last session" affordance.
 */
function readStoredFiltersFrom(storage: Storage): Filters | null {
  try {
    const raw = storage.getItem(StorageKey.FILTERS);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    const result = FilterSchema.safeParse(parsed);
    if (!result.success) {
      // Schema drift — drop silently so the user gets a clean default on
      // the next session. Logging keeps the event observable in dev.
      // eslint-disable-next-line no-console
      console.info(
        '[useUrlFilters] dropping stored filters (schema drift)',
        result.error.issues,
      );
      try {
        storage.removeItem(StorageKey.FILTERS);
      } catch {
        /* ignore — storage might be sandboxed */
      }
      return null;
    }
    return result.data;
  } catch {
    // Privacy-mode storage, invalid JSON, or access denied — just
    // fall through to defaults without surfacing an error.
    return null;
  }
}

/** Persist current filters to storage. Safe to call in every commit.
 *
 *  Writes to BOTH sessionStorage (source of truth for auto-rehydrate) and
 *  localStorage (durable copy for an explicit "restore" flow, not used by
 *  the hydration path — see Overview P1-2 regression note in the module
 *  doc). stripDefaults keeps the stored JSON compact and in sync with the
 *  URL-emission contract. */
function writeStoredFilters(f: Filters): void {
  const stripped = stripDefaults(f);
  const payload = JSON.stringify(stripped);
  try {
    sessionStorage.setItem(StorageKey.FILTERS, payload);
  } catch {
    /* ignore — storage quota / privacy mode */
  }
  try {
    localStorage.setItem(StorageKey.FILTERS, payload);
  } catch {
    /* ignore — storage quota / privacy mode */
  }
}

/** True when `search` (minus `?`) contains zero query keys. */
function isEmptySearch(search: string): boolean {
  const stripped = search.startsWith('?') ? search.slice(1) : search;
  return stripped.trim().length === 0;
}

// ---------------------------------------------------------------------------
// BUG-1 — module-level hydration flag
// ---------------------------------------------------------------------------
// The hook is mounted in many places simultaneously (FilterBar at the layout
// root, every page, SortableTable, use-user-detail, …). Each instance used
// to own its own `useRef(false)` — which meant hydration re-ran whenever the
// page component unmounted/remounted (e.g. tab switch), and the stale
// storage snapshot could overwrite filters the user had just changed. A
// module-level flag fires hydration EXACTLY ONCE per page load, for the
// first hook that mounts on an empty URL. Any subsequent hook instances
// short-circuit immediately.
let hasHydratedFromStorage = false;

/**
 * Test-only escape hatch: reset the module-level hydration flag so each
 * `renderHook` invocation starts fresh. Production code MUST NOT call this
 * — hydration is intentionally one-shot per page load.
 */
export function __resetHydrationFlagForTesting(): void {
  hasHydratedFromStorage = false;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseUrlFiltersResult {
  /** Committed filter state — drives API calls. */
  filters: Filters;
  /**
   * Legacy alias for `filters`. Live-apply (Bug F1) removed the staging
   * buffer — `pendingFilters` always equals `filters`. Kept so downstream
   * controls (DateRangeControl, FilterModal) can continue to bind to it
   * without a rename.
   */
  pendingFilters: Filters;
  /**
   * Apply a partial patch to the URL immediately — every control edit
   * triggers a refetch without an intermediate Apply step.
   */
  setPending: (patch: Partial<Filters>) => void;
  /**
   * Legacy no-op (kept for FilterBar's Apply handler, which was removed in
   * the same change). Live-apply means there's nothing to flush — calling
   * this is safe and does nothing.
   */
  apply: () => void;
  /**
   * Alias for `setPending`. Historically commit wrote directly to the URL
   * bypassing pending; with live-apply both paths are equivalent. Kept so
   * <SortableTable> (Plan 03-05) doesn't need updating.
   */
  commit: (patch: Partial<Filters>) => void;
  /** Clear the URL back to empty. */
  reset: () => void;
  /**
   * Legacy flag — always `false` under live-apply. Kept so any consumer
   * binding it to a disabled-state still compiles; all current call sites
   * either ignore it or drive the removed Apply button.
   */
  dirty: boolean;
}

export function useUrlFilters(): UseUrlFiltersResult {
  const [, setSearchParams] = useSearchParams();
  const { search } = useLocation();

  // `filters` is the committed state — memoised so it's a stable reference
  // while the URL query string doesn't change. We read the raw `location.search`
  // string (not URLSearchParams.toString()) to preserve unencoded commas so
  // `qs.parse({ comma: true })` can split array values correctly.
  const filters = useMemo<Filters>(() => parseFromSearch(search), [search]);

  // Bug D16 — hydrate from SESSIONStorage on first mount when the URL is
  // empty. BUG-1 (2026-04-23): the flag is MODULE-LEVEL, not per-hook-
  // instance — see `hasHydratedFromStorage` above. With the previous
  // `useRef(false)` guard, every consumer of this hook ran its own
  // hydration pass on mount, so a tab switch (unmount/remount of the page's
  // hook instance) could re-hydrate from a now-stale storage snapshot and
  // overwrite the URL the user had just edited.
  //
  // Overview P1-2 (2026-04-23): the source of the auto-rehydrate was
  // switched from localStorage → sessionStorage. localStorage is shared
  // across tabs AND survives browser restart, so a user with e.g.
  // `dayFilter=weekdays` cached there would see weekday-filtered data
  // whenever they clicked ANY bare dashboard URL — including deep-links
  // a colleague sent them. Copy-URL sharing then showed different data
  // to sender and recipient, breaking the core "share a URL" promise.
  // sessionStorage is per-tab, so: (1) the same-tab convenience of
  // "remember my filters when I navigate away and come back" is
  // preserved; (2) a new tab opened on a shared URL sees DEFAULT_FILTERS
  // like any fresh session, and the URL itself is the only source of
  // truth for shared-link recipients.
  //
  // URL always wins over storage: if the user lands on `/overview?role=rm`,
  // storage is ignored here and the current URL stays authoritative.
  useEffect(() => {
    if (hasHydratedFromStorage) return;
    hasHydratedFromStorage = true;
    if (!isEmptySearch(search)) return;
    // Read from sessionStorage ONLY — localStorage is no longer consulted
    // for auto-rehydrate (see above). The durable localStorage copy still
    // exists so a future explicit "Restore last session" control can read
    // it, but bare URLs now always render DEFAULT_FILTERS on a fresh tab.
    const stored = readStoredFiltersFrom(sessionStorage);
    if (!stored) return;
    const next = writeToUrl(stored);
    // Only push if the hydrated URL is non-empty — writing `?` on every
    // fresh visit would cause an unnecessary history entry and defeat
    // the stripDefaults contract.
    if ([...next.keys()].length === 0) return;
    // BUG F2 / filter-first-click: use functional form + the merger so we
    // hydrate filter keys onto whatever the URL already has. The empty-URL
    // guard above means this is almost always a no-op merge, but the
    // functional path is still the correct shape in case another hook has
    // already written (e.g. deep-link `?userDetail=abc` with empty filter
    // state).
    setSearchParams(
      (prev) => mergeFiltersIntoParams(prev, stored),
      { replace: true },
    );
  }, [search, setSearchParams]);

  // Bug D16 — persist every committed state. We write on the tail end
  // of render (after URL already changed) so `filters` reflects the
  // latest committed shape. Empty URL intentionally writes an empty
  // `{}` payload, which on next hydration short-circuits via the
  // `[...next.keys()].length === 0` check above (→ DEFAULT_FILTERS).
  useEffect(() => {
    writeStoredFilters(filters);
  }, [filters]);

  // BUG-1 (2026-04-23) — cross-tab `storage` event sync REMOVED.
  //
  // The previous listener mirrored `storage` events from other tabs into
  // this tab's URL via `setSearchParams`. In practice that produced the
  // exact "URL changes but filter control doesn't react" symptom the user
  // reported: a background tab could rewrite the foreground tab's URL
  // mid-click (e.g. the write-back effect in the other tab fires after the
  // user has already queued a `setPending` here). Different tabs having
  // different filter views is perfectly reasonable UX; surprising URL
  // rewrites are not. Per-tab divergence is now a feature.

  // Live-apply: merge the patch onto the *current* URL-parsed filters and
  // push straight to setSearchParams. No intermediate buffer — no staleness.
  // BUG F2 / filter-first-click: functional setSearchParams + a merger that
  // only touches FILTER_URL_KEYS + *_sort. This preserves sibling-hook state
  // (e.g. Feedback tab's sort/limit/hasText written by useFeedbackUrlState)
  // that previously got blown away on every global-filter edit, which made
  // feedback-tab filters look like they required a second click to apply
  // (the first click wrote sort=best, the following global-filter tick or
  // storage-listener wiped it, and only the second click survived because
  // by then there was nothing else in flight to clobber it).
  const setPending = useCallback(
    (patch: Partial<Filters>) => {
      const next = { ...filters, ...patch };
      // Admins → Excluded also clears the user chips (2026-08-19).
      //
      // While Admins is Included the autocomplete offers admin names, so a
      // chip naming an admin is now one click away. Flip back to Excluded and
      // the two filters contradict each other in SQL — `user_id IN ('<admin>')`
      // AND `slack_id NOT IN (admins)` — so every number on every tab reads
      // zero with nothing on screen saying why. The two causes sit at opposite
      // ends of a bar that wraps and neither mentions the other; an all-zero
      // dashboard is the one outcome this must not be able to produce.
      //
      // It clears ALL chips, not just the admin ones: admin-ness is not on the
      // wire (`users[]` is bare usernames, and a chip can also be typed by
      // hand or arrive in a URL), so there is nothing to tell them apart. The
      // cost is visible — chips disappear from the bar and one click puts a
      // non-admin one back — where the failure it replaces is silent.
      //
      // Only on the actual transition, so re-clicking the segment the filter
      // already sits on does not eat a selection. URL contract is unaffected:
      // `stripDefaults` omits `users` when empty, so this drops the key.
      if (filters.admins === 'included' && next.admins === 'excluded') {
        next.users = [];
      }
      setSearchParams((prev) => mergeFiltersIntoParams(prev, next), {
        replace: false,
      });
    },
    [filters, setSearchParams],
  );

  // `apply` is now a no-op — controls already commit on change. Kept as
  // `useCallback(() => {})` rather than deleted to preserve the returned
  // interface for callers still passing it to a button handler.
  const apply = useCallback(() => {
    // intentional no-op (live-apply — see module doc)
  }, []);

  // `commit` is an alias for `setPending` now; under staged filters it
  // bypassed the buffer, which is trivially the same thing as live-apply.
  const commit = setPending;

  // Reset ONLY the filter-owned keys. Sibling-hook state (feedback tab
  // sort/limit, user-detail deep link, …) survives so global-Reset doesn't
  // surprise the user by clearing unrelated URL state.
  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Array.from(next.keys())) {
          if (isFilterKey(key)) next.delete(key);
        }
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return {
    filters,
    pendingFilters: filters,
    setPending,
    apply,
    commit,
    reset,
    dirty: false,
  };
}
